
import React from 'react';
import { BuybackRequest, AppSettings } from '../types';

interface PrintableTradeInAgreementProps {
  buybackRequest: BuybackRequest;
  appSettings: AppSettings;
}

export const PrintableTradeInAgreement: React.FC<PrintableTradeInAgreementProps> = ({ buybackRequest, appSettings }) => {
  if (!buybackRequest || !appSettings) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  return (
    <div className="p-4 font-sans text-xs leading-normal bg-white text-black">
      {appSettings.logoBase64 && (
        <div className="text-center mb-4">
          <img 
            src={appSettings.logoBase64} 
            alt={`${appSettings.companyName} Logo`} 
            className="mx-auto h-16 max-h-20 object-contain" 
          />
        </div>
      )}
      <h1 className="text-lg font-bold text-center mb-1">{appSettings.companyName}</h1>
      <h2 className="text-md font-semibold text-center mb-4">Device Trade-In Agreement</h2>

      <div className="grid grid-cols-2 gap-x-4 mb-4 text-xs">
        <div><strong>Request ID:</strong> {buybackRequest.id}</div>
        <div><strong>Date:</strong> {formatDate(buybackRequest.createdAt)}</div>
        <div><strong>Customer Name:</strong> {buybackRequest.customerName}</div>
        {buybackRequest.customerEmail && <div><strong>Email:</strong> {buybackRequest.customerEmail}</div>}
        {buybackRequest.customerPhone && <div><strong>Phone:</strong> {buybackRequest.customerPhone}</div>}
      </div>

      <h3 className="font-semibold text-sm my-2">Device Details:</h3>
      <div className="grid grid-cols-2 gap-x-4 mb-4 text-xs border p-2 rounded-md">
        <div><strong>Device Type:</strong> {buybackRequest.deviceType}</div>
        <div><strong>Model:</strong> {buybackRequest.deviceModel}</div>
        {buybackRequest.storageCapacity && <div><strong>Storage:</strong> {buybackRequest.storageCapacity}</div>}
        {buybackRequest.imeiOrSerial && <div><strong>IMEI/Serial:</strong> {buybackRequest.imeiOrSerial}</div>}
        <div className="col-span-2"><strong>Stated Condition:</strong> {buybackRequest.overallCondition}</div>
      </div>

      <div className="my-4 p-3 border border-slate-300 rounded-md bg-slate-50 text-xs">
        <h3 className="font-semibold mb-1">Trade-In Value:</h3>
        <p className="text-lg font-bold text-[var(--color-primary)]">
          Final Offered Value: ${buybackRequest.finalOfferedValue?.toFixed(2) || 'N/A (Pending Finalization)'}
        </p>
        {buybackRequest.finalOfferedValue !== undefined && buybackRequest.estimatedValue !== buybackRequest.finalOfferedValue && (
            <p className="text-xs italic mt-1">Original Estimate: ${buybackRequest.estimatedValue.toFixed(2)}</p>
        )}
      </div>
      
      <div className="mb-4 p-3 border border-slate-300 rounded-md bg-slate-50 text-xs">
        <h3 className="font-semibold mb-1">Customer Agreement:</h3>
        <p className="whitespace-pre-wrap">
          I, {buybackRequest.customerName}, confirm that I am the legal owner of the device detailed above and have the right to trade it in. 
          I acknowledge that all personal data has been removed from the device and any account locks (e.g., Find My Device, iCloud Lock) have been disabled. 
          {appSettings.companyName} is not responsible for any data remaining on the device. 
          I accept the final offered trade-in value of ${buybackRequest.finalOfferedValue?.toFixed(2) || 'N/A'}. 
          This transaction is final.
        </p>
      </div>

      <div className="mt-8 space-y-6 text-xs">
        <div className="flex justify-between items-end">
          <div className="w-2/5">
            <hr className="border-black mb-1" />
            <p>Customer Signature</p>
          </div>
          <div className="w-1/4">
            <hr className="border-black mb-1" />
            <p>Date</p>
          </div>
        </div>
        <div className="flex justify-start items-end pt-4">
            <div className="w-2/5">
                <hr className="border-black mb-1" />
                <p>{appSettings.companyName} Representative</p>
            </div>
        </div>
      </div>

      <p className="text-center text-[8pt] mt-6 text-slate-500">
        Thank you for your trade-in!
      </p>
    </div>
  );
};
